import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  EllipsisVertical,
  Pause,
  Plus,
  RefreshCcw,
  Repeat,
  Settings,
  StopCircle,
  Target,
  Trash2,
} from "lucide-react";
import Table from "@/components/ui/shared/table/Table";
import TableBody from "@/components/ui/shared/table/TableBody";
import TableCell from "@/components/ui/shared/table/TableCell";
import TableHeader from "@/components/ui/shared/table/TableHeader";
import TableRow from "@/components/ui/shared/table/TableRow";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FOLLOW_UP_STATUS_LABELS, displayFollowUpStatus, isIntentHeld } from "@/lib/followUpStatus";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@/lib/supabase";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

type FollowUpRow = Database["public"]["Tables"]["account_assistant_follow_ups"]["Row"] & {
  assistant?: { id: string; name: string } | null;
};
type IntervalRow = Database["public"]["Tables"]["account_assistant_follow_up_intervals"]["Row"];
type StateRow = Database["public"]["Tables"]["account_contact_follow_up_states"]["Row"] & {
  contact?: {
    id: string;
    name: string | null;
    display_name: string | null;
    push_name_last: string | null;
    phone: string | null;
    follow_up_opt_out?: boolean;
  } | null;
  follow_up?: { id: string; name: string } | null;
};

type TabKey = "sequencias" | "monitor";

const STATUS_LABELS = FOLLOW_UP_STATUS_LABELS;

const statusDetail = (state: StateRow): string => {
  const t = i18n.getFixedT(null, "automations");

  // Antes de tudo: segurado pela guarda. No banco o status ainda é 'active', e
  // sem este ramo a linha apareceria como "Em cadência" com um disparo marcado
  // que nunca acontece — o pior tipo de estado, o silencioso.
  if (isIntentHeld(state)) {
    const until = new Date(state.intent_hold_until as string).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    const base = t("followUp.detailIntentHeld", { time: until });
    return state.intent_hold_reason ? `${base} ${state.intent_hold_reason}` : base;
  }

  if (state.status === "paused") {
    if (state.next_fire_reason === "human_active") return t("followUp.detailHumanActive");
    if (state.next_fire_reason === "excluded_tag") return t("followUp.detailExcludedTag");
    if (state.next_fire_reason === "assistant_paused") return t("followUp.detailAssistantPaused");
    return state.last_error || t("followUp.detailPaused");
  }
  if (state.status === "stopped") {
    if (state.stop_reason === "opt_out") return t("followUp.detailOptOut");
    if (state.stop_reason === "won_lost") return t("followUp.detailWonLost");
    if (state.stop_reason === "manual") return t("followUp.detailManual");
    if (state.stop_reason === "intent_no_pending") return t("followUp.detailIntentStopped");
    return state.stop_reason || t("followUp.detailStopped");
  }
  if (state.status === "goal_met") {
    return state.stop_reason === "goal_stage" ? t("followUp.detailGoalStage") : t("followUp.detailGoalTag");
  }
  if (state.status === "completed") {
    return state.stop_reason === "cycles_exhausted"
      ? t("followUp.detailCyclesDone")
      : state.stop_reason === "no_steps"
        ? t("followUp.detailNoSteps")
        : t("followUp.detailCompleted");
  }
  if (state.status === "blocked") return state.last_error || t("followUp.detailWaitingAi");
  if (state.next_fire_reason === "stale") return t("followUp.detailStale");
  if (state.next_fire_reason === "before_activation") return "Conversa anterior à ativação da sequência";
  return state.last_error || "";
};

const formatRelative = (iso: string | null): string => {
  if (!iso) return "—";
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) return "agora";
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `em ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `em ${hours}h ${mins % 60}m`;
  return `em ${Math.round(hours / 24)}d`;
};

const contactLabel = (state: StateRow): string =>
  state.contact?.name ||
  state.contact?.display_name ||
  state.contact?.push_name_last ||
  state.contact?.phone ||
  "Contato";

const FollowUps: React.FC = () => {
  const { t } = useTranslation("automations");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState<TabKey>("sequencias");
  const [followUps, setFollowUps] = useState<FollowUpRow[]>([]);
  const [intervalsByFollowUp, setIntervalsByFollowUp] = useState<Record<string, IntervalRow[]>>({});
  const [states, setStates] = useState<StateRow[]>([]);
  const [stateCounts, setStateCounts] = useState<Record<string, Record<string, number>>>({});
  const [loading, setLoading] = useState(true);
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const [monitorStatusFilter, setMonitorStatusFilter] = useState<string>("all");
  const [monitorFollowUpFilter, setMonitorFollowUpFilter] = useState<string>("all");

  const loadData = useCallback(async () => {
    if (!user?.account_id || !supabase) return;
    setLoading(true);
    try {
      const [followUpsRes, statesRes] = await Promise.all([
        supabase
          .from("account_assistant_follow_ups")
          .select("*, assistant:account_assistants(id, name)")
          .eq("account_id", user.account_id)
          .order("created_at", { ascending: false }),
        supabase
          .from("account_contact_follow_up_states")
          .select(
            "*, contact:account_contacts(id, name, display_name, push_name_last, phone, follow_up_opt_out), follow_up:account_assistant_follow_ups(id, name)"
          )
          .eq("account_id", user.account_id)
          .order("updated_at", { ascending: false })
          .range(0, 4999),
      ]);

      if (followUpsRes.error) throw followUpsRes.error;
      if (statesRes.error) throw statesRes.error;

      const followUpRows = (followUpsRes.data || []) as FollowUpRow[];
      setFollowUps(followUpRows);

      const stateRows = (statesRes.data || []) as StateRow[];
      setStates(stateRows);

      const counts: Record<string, Record<string, number>> = {};
      for (const state of stateRows) {
        const byStatus = (counts[state.follow_up_id] = counts[state.follow_up_id] || {});
        byStatus[state.status] = (byStatus[state.status] || 0) + 1;
      }
      setStateCounts(counts);

      const followUpIds = followUpRows.map((item) => item.id);
      if (followUpIds.length > 0) {
        const { data: intervalsData, error: intervalsError } = await supabase
          .from("account_assistant_follow_up_intervals")
          .select("*")
          .in("follow_up_id", followUpIds)
          .order("sequence_order", { ascending: true });
        if (intervalsError) throw intervalsError;

        const grouped: Record<string, IntervalRow[]> = {};
        for (const interval of (intervalsData || []) as IntervalRow[]) {
          (grouped[interval.follow_up_id] = grouped[interval.follow_up_id] || []).push(interval);
        }
        setIntervalsByFollowUp(grouped);
      } else {
        setIntervalsByFollowUp({});
      }
    } catch (error) {
      console.error("Erro ao carregar follow-ups:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.account_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totals = useMemo(() => {
    const acc = { active: 0, paused: 0, goal_met: 0, stopped: 0 };
    for (const state of states) {
      if (state.status === "active") acc.active += 1;
      else if (state.status === "paused" || state.status === "blocked") acc.paused += 1;
      else if (state.status === "goal_met") acc.goal_met += 1;
      else if (state.status === "stopped" || state.status === "completed") acc.stopped += 1;
    }
    return acc;
  }, [states]);

  const monitorRows = useMemo(() => {
    return states.filter((state) => {
      if (monitorFollowUpFilter !== "all" && state.follow_up_id !== monitorFollowUpFilter) return false;
      if (monitorStatusFilter === "all") return true;
      if (monitorStatusFilter === "paused") return state.status === "paused" || state.status === "blocked";
      return state.status === monitorStatusFilter;
    });
  }, [states, monitorStatusFilter, monitorFollowUpFilter]);

  const handleToggleActive = async (followUp: FollowUpRow) => {
    if (!supabase) return;
    const { error } = await supabase
      .from("account_assistant_follow_ups")
      .update({ is_active: !followUp.is_active })
      .eq("id", followUp.id);
    if (error) {
      console.error("Erro ao atualizar follow-up:", error);
      return;
    }
    setFollowUps((prev) =>
      prev.map((item) => (item.id === followUp.id ? { ...item, is_active: !followUp.is_active } : item))
    );
  };

  const handleDelete = async (id: string) => {
    if (!supabase) return;
    if (!confirm(t("followUp.confirmRemove"))) return;
    const { error } = await supabase.from("account_assistant_follow_ups").delete().eq("id", id);
    if (error) {
      console.error("Erro ao remover follow-up:", error);
      return;
    }
    setOpenPopoverId(null);
    await loadData();
  };

  const handleStopState = async (state: StateRow) => {
    if (!supabase) return;
    const { error } = await supabase
      .from("account_contact_follow_up_states")
      .update({
        status: "stopped",
        stop_reason: "manual",
        next_fire_at: null,
        next_fire_reason: null,
        dispatch_locked_until: null,
      })
      .eq("id", state.id);
    if (error) {
      console.error("Erro ao encerrar cadência:", error);
      return;
    }
    await loadData();
  };

  const handleRestartState = async (state: StateRow) => {
    if (!supabase) return;
    if (state.stop_reason === "opt_out" && state.contact?.id) {
      await supabase
        .from("account_contacts")
        .update({ follow_up_opt_out: false })
        .eq("id", state.contact.id);
    }
    const { error } = await supabase
      .from("account_contact_follow_up_states")
      .update({
        status: "active",
        stop_reason: null,
        next_interval_order: 0,
        cycle_count: 0,
        cycle_cooldown_until: null,
        next_fire_at: null,
        next_fire_reason: null,
        last_error: null,
        dispatch_locked_until: null,
      })
      .eq("id", state.id);
    if (error) {
      console.error("Erro ao reiniciar cadência:", error);
      return;
    }
    await loadData();
  };

  const kpiCards = [
    { label: t("followUp.kpiActive"), value: totals.active, icon: <Repeat size={18} />, tone: "text-[#2563EB] dark:text-[#60A5FA] bg-[#DBEAFE] dark:bg-blue-500/20" },
    { label: t("followUp.kpiPaused"), value: totals.paused, icon: <Pause size={18} />, tone: "text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/15" },
    { label: t("followUp.kpiGoalMet"), value: totals.goal_met, icon: <Target size={18} />, tone: "text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-500/15" },
    { label: t("followUp.kpiStopped"), value: totals.stopped, icon: <CheckCircle2 size={18} />, tone: "text-slate-600 dark:text-neutral-300 bg-slate-100 dark:bg-neutral-500/15" },
  ];

  return (
    <div className="w-full flex min-h-[calc(100dvh-120px)] flex-col gap-6 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[34px] sm:text-[36px] leading-tight font-bold text-foreground tracking-tight">
          {t("followUp.listTitle")}
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="min-h-[40px] px-3 rounded-[12px] flex justify-center items-center gap-2 border border-border bg-card hover:bg-[#F1F5F9] text-[#475569] transition-colors dark:hover:bg-[#27272A] dark:text-neutral-300 dark:rounded-[6px]"
            title={t("common.refresh")}
          >
            <RefreshCcw size={16} />
          </button>
          <button
            onClick={() => navigate("/follow-ups/novo")}
            className="min-h-[40px] px-4 rounded-[12px] flex justify-center items-center gap-2 transition-colors bg-[#2563EB] hover:bg-[#1D4ED8] text-white shadow-sm dark:border dark:border-neutral-500 dark:bg-neutral-600 dark:hover:bg-neutral-500 dark:shadow-none dark:rounded-[6px]"
          >
            <p className="font-medium text-[14px]">{t("followUp.newSequence")}</p>
            <Plus size={18} className="opacity-80 dark:text-[#A1A1AA] dark:opacity-100" />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpiCards.map((kpi) => (
          <div
            key={kpi.label}
            className="flex items-center gap-3 p-4 rounded-[16px] border border-border bg-card shadow-sm dark:shadow-none"
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${kpi.tone}`}>
              {kpi.icon}
            </div>
            <div>
              <p className="text-[22px] font-bold text-foreground leading-none tabular-nums">{kpi.value}</p>
              <p className="text-xs text-[#64748B] dark:text-neutral-400 mt-1">{kpi.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {([
          { key: "sequencias", label: t("followUp.tabSequences") },
          { key: "monitor", label: t("followUp.tabMonitor") },
        ] as { key: TabKey; label: string }[]).map((item) => (
          <button
            key={item.key}
            onClick={() => setTab(item.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === item.key
                ? "border-[#2563EB] text-[#2563EB] dark:border-[#60A5FA] dark:text-[#60A5FA]"
                : "border-transparent text-[#64748B] hover:text-[#0F172A] dark:text-neutral-400 dark:hover:text-neutral-50"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="w-full flex-1 flex items-center justify-center border border-border rounded-[20px] bg-card shadow-sm min-h-[240px]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-6 h-6 border-3 border-border border-t-primary rounded-full animate-spin" />
            <p className="text-muted-foreground text-sm">{t("followUp.listLoading")}</p>
          </div>
        </div>
      ) : tab === "sequencias" ? (
        followUps.length === 0 ? (
          <div className="w-full flex-1 flex items-center justify-center border border-border rounded-[20px] bg-card shadow-sm min-h-[280px]">
            <div className="flex flex-col items-center gap-5 text-center p-8 max-w-md">
              <div className="w-16 h-16 bg-[#DBEAFE] rounded-full flex items-center justify-center dark:bg-blue-500/20">
                <Repeat className="w-8 h-8 text-[#2563EB] dark:text-[#60A5FA]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2 dark:text-neutral-50">
                  {t("followUp.listEmpty")}
                </h3>
                <p className="text-[#64748B] text-sm dark:text-neutral-400">
                  {t("followUp.listEmptyHint")}
                </p>
              </div>
              <button
                onClick={() => navigate("/follow-ups/novo")}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-[12px] shadow-sm transition-colors duration-200"
              >
                <Plus size={18} />
                {t("followUp.createSequence")}
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full overflow-auto border border-border rounded-[20px] bg-card shadow-sm table-scrollbar">
            <Table className="min-w-[860px]">
              <TableHeader className="border-b border-border sticky top-0 bg-card z-10">
                <TableRow>
                  {[t("followUp.thSequence"), t("followUp.thAssistant"), t("followUp.thSteps"), t("followUp.thInCadence"), t("followUp.thGoals"), t("followUp.thActive"), t("followUp.thActions")].map((item) => (
                    <TableCell key={item} as="th" className="!text-[#475569] !font-semibold !text-[13px] !py-4">
                      <p>{item}</p>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {followUps.map((followUp) => {
                  const intervals = intervalsByFollowUp[followUp.id] || [];
                  const counts = stateCounts[followUp.id] || {};
                  const inCadence = (counts.active || 0) + (counts.paused || 0) + (counts.blocked || 0);
                  return (
                    <TableRow
                      key={followUp.id}
                      className="hover:bg-[#F8FAFC] dark:hover:bg-hover transition-colors duration-200 border-b border-border/60 last:border-b-0"
                    >
                      <TableCell as="td" className="!py-4">
                        <div className="flex flex-col gap-1">
                          <p className="font-semibold text-[#0F172A] dark:text-neutral-50 dark:font-medium">
                            {followUp.name}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {(followUp.goal_pipeline_stage_id || (followUp.goal_tags || []).length > 0) ? (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-medium dark:bg-green-500/15 dark:text-green-400">
                                <Target size={11} />
                                {followUp.goal_pipeline_stage_id ? t("followUp.goalStage") : t("followUp.goalTags", { tags: (followUp.goal_tags || []).join(", ") })}
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] dark:bg-neutral-500/15 dark:text-neutral-400">
                                {t("followUp.noGoalSet")}
                              </span>
                            )}
                            {followUp.max_cycles > 1 && (
                              <span className="px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#2563EB] text-[11px] dark:bg-blue-500/20 dark:text-[#60A5FA]">
                                {followUp.max_cycles} ciclos
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell as="td" className="!py-4">
                        <p className="text-sm text-[#0F172A] dark:text-neutral-50">
                          {followUp.assistant?.name || "—"}
                        </p>
                      </TableCell>
                      <TableCell as="td" className="!py-4">
                        <p className="text-sm text-[#0F172A] dark:text-neutral-50 tabular-nums">
                          {t("followUp.stepCount", { count: intervals.length })}
                        </p>
                      </TableCell>
                      <TableCell as="td" className="!py-4">
                        <p className="text-sm text-[#0F172A] dark:text-neutral-50 tabular-nums">{inCadence}</p>
                      </TableCell>
                      <TableCell as="td" className="!py-4">
                        <p className="text-sm font-semibold text-green-600 dark:text-green-400 tabular-nums">
                          {counts.goal_met || 0}
                        </p>
                      </TableCell>
                      <TableCell as="td" className="!py-4">
                        <Switch
                          checked={followUp.is_active}
                          onCheckedChange={() => handleToggleActive(followUp)}
                        />
                      </TableCell>
                      <TableCell as="td" className="!py-4">
                        <Popover
                          open={openPopoverId === followUp.id}
                          onOpenChange={(open) => setOpenPopoverId(open ? followUp.id : null)}
                        >
                          <PopoverTrigger asChild>
                            <button className="w-[34px] h-[34px] flex justify-center items-center border border-border bg-white hover:bg-[#F1F5F9] rounded-[10px] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:hover:bg-[#27272A]">
                              <EllipsisVertical size={18} className="text-[#64748B] dark:text-neutral-400" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="!bg-card p-1 w-[140px] flex flex-col gap-1 border-border border rounded-xl shadow-md dark:!bg-[#18181B] dark:border-[#3F3F46] dark:rounded-[7px] dark:shadow-none">
                            <button
                              onClick={() => {
                                setOpenPopoverId(null);
                                navigate(`/follow-ups/editar/${followUp.id}`);
                              }}
                              className="w-full flex items-center gap-2 px-3 rounded-[7px] bg-transparent hover:bg-[#F1F5F9] min-h-[32px] text-foreground transition-colors duration-200 dark:hover:bg-[#27272A] dark:text-neutral-50"
                            >
                              <Settings size={16} className="text-[#2563EB] dark:text-[#60A5FA]" />
                              {t("common.edit")}
                            </button>
                            <div className="h-px bg-border mx-1 dark:bg-[#3F3F46]" />
                            <button
                              onClick={() => handleDelete(followUp.id)}
                              className="w-full flex items-center gap-2 px-3 rounded-[7px] bg-transparent hover:bg-red-50 min-h-[32px] text-red-500 transition-colors duration-200 dark:hover:bg-[#27272A] dark:text-red-400"
                            >
                              <Trash2 size={16} />
                              {t("common.remove")}
                            </button>
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={monitorFollowUpFilter}
              onChange={(e) => setMonitorFollowUpFilter(e.target.value)}
              className="h-[38px] px-3 rounded-[10px] border border-border bg-card text-sm text-foreground outline-none dark:bg-[#27272A]"
            >
              <option value="all">{t("followUp.allSequences")}</option>
              {followUps.map((followUp) => (
                <option key={followUp.id} value={followUp.id}>
                  {followUp.name}
                </option>
              ))}
            </select>
            <select
              value={monitorStatusFilter}
              onChange={(e) => setMonitorStatusFilter(e.target.value)}
              className="h-[38px] px-3 rounded-[10px] border border-border bg-card text-sm text-foreground outline-none dark:bg-[#27272A]"
            >
              <option value="all">{t("followUp.allStatuses")}</option>
              <option value="active">{t("followUp.statusRunning")}</option>
              <option value="paused">{t("followUp.statusPaused")}</option>
              <option value="goal_met">{t("followUp.statusGoalMet")}</option>
              <option value="completed">{t("followUp.statusDone")}</option>
              <option value="stopped">{t("followUp.statusStopped")}</option>
            </select>
            <span className="text-xs text-[#64748B] dark:text-neutral-400 ml-1">
              {monitorRows.length} {monitorRows.length === 1 ? "contato" : "contatos"}
            </span>
          </div>

          <div className="w-full overflow-auto border border-border rounded-[20px] bg-card shadow-sm table-scrollbar max-h-[calc(100dvh-430px)]">
            <Table className="min-w-[900px]">
              <TableHeader className="border-b border-border sticky top-0 bg-card z-10">
                <TableRow>
                  {[t("followUp.thContact"), t("followUp.thSequence"), t("followUp.thStage"), t("followUp.thCycle"), t("followUp.thNextFire"), t("followUp.thStatus"), t("followUp.thActions")].map((item) => (
                    <TableCell key={item} as="th" className="!text-[#475569] !font-semibold !text-[13px] !py-4">
                      <p>{item}</p>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {monitorRows.length === 0 ? (
                  <TableRow>
                    <TableCell as="td" className="!py-8" colSpan={7}>
                      <p className="text-center text-sm text-[#64748B] dark:text-neutral-400">
                        {t("followUp.noContactsFilter")}
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  monitorRows.slice(0, 300).map((state) => {
                    const intervals = intervalsByFollowUp[state.follow_up_id] || [];
                    const totalSteps = intervals.length;
                    const shownStatus = displayFollowUpStatus(state);
                    const statusInfo = STATUS_LABELS[shownStatus] || STATUS_LABELS.active;
                    const detail = statusDetail(state);
                    const isTerminal = ["stopped", "completed", "goal_met"].includes(state.status);
                    return (
                      <TableRow
                        key={state.id}
                        className="hover:bg-[#F8FAFC] dark:hover:bg-hover transition-colors duration-200 border-b border-border/60 last:border-b-0"
                      >
                        <TableCell as="td" className="!py-3">
                          <button
                            onClick={() => state.contact?.id && navigate(`/chat/${state.contact.id}`)}
                            className="font-medium text-[#0F172A] dark:text-neutral-50 hover:text-[#2563EB] dark:hover:text-[#60A5FA] transition-colors text-left"
                          >
                            {contactLabel(state)}
                          </button>
                        </TableCell>
                        <TableCell as="td" className="!py-3">
                          <p className="text-sm text-[#475569] dark:text-neutral-300">
                            {state.follow_up?.name || "—"}
                          </p>
                          {state.enrollment_source === "manual" && (
                            <span className="text-[11px] text-[#64748B] dark:text-neutral-400">
                              {t("followUp.manualEnrollment")}
                            </span>
                          )}
                        </TableCell>
                        <TableCell as="td" className="!py-3">
                          <div className="flex items-center gap-2 min-w-[110px]">
                            <div className="flex-1 h-[5px] rounded-full bg-slate-100 dark:bg-neutral-700 overflow-hidden max-w-[70px]">
                              <div
                                className={`h-full rounded-full ${state.status === "goal_met" ? "bg-green-500" : "bg-[#2563EB] dark:bg-[#60A5FA]"}`}
                                style={{
                                  width: totalSteps > 0
                                    ? `${Math.min(100, (Math.min(state.next_interval_order, totalSteps) / totalSteps) * 100)}%`
                                    : "0%",
                                }}
                              />
                            </div>
                            <span className="text-xs text-[#64748B] dark:text-neutral-400 tabular-nums whitespace-nowrap">
                              {Math.min(state.next_interval_order, totalSteps)}/{totalSteps}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell as="td" className="!py-3">
                          <p className="text-xs text-[#64748B] dark:text-neutral-400 tabular-nums">
                            {(state.cycle_count ?? 0) + (isTerminal ? 0 : 1)}
                          </p>
                        </TableCell>
                        <TableCell as="td" className="!py-3">
                          <p className="text-sm text-[#0F172A] dark:text-neutral-50">
                            {state.status === "active" ? formatRelative(state.next_fire_at) : "—"}
                          </p>
                        </TableCell>
                        <TableCell as="td" className="!py-3">
                          <div className="flex flex-col gap-0.5">
                            <span className={`w-fit px-2 py-0.5 rounded-full text-[11px] font-medium ${statusInfo.className}`}>
                              {t(statusInfo.labelKey)}
                            </span>
                            {detail && (
                              <span className="text-[11px] text-[#94A3B8] dark:text-neutral-500 max-w-[220px] truncate" title={detail}>
                                {detail}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell as="td" className="!py-3">
                          <div className="flex items-center gap-1.5">
                            {!isTerminal ? (
                              <button
                                onClick={() => handleStopState(state)}
                                className="flex items-center gap-1 px-2 py-1 rounded-[8px] text-[12px] text-red-500 hover:bg-red-50 transition-colors dark:text-red-400 dark:hover:bg-[#27272A]"
                                title={t("followUp.endForContact")}
                              >
                                <StopCircle size={14} />
                                {t("followUp.end")}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleRestartState(state)}
                                className="flex items-center gap-1 px-2 py-1 rounded-[8px] text-[12px] text-[#2563EB] hover:bg-[#EEF4FF] transition-colors dark:text-[#60A5FA] dark:hover:bg-[#27272A]"
                                title={t("followUp.restartFromZero")}
                              >
                                <RefreshCcw size={14} />
                                {t("followUp.restart")}
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUps;
